import { ICONS } from '../icon-map';

/*
 * The product's navigation: one source for the sidebar, the command palette, the mobile
 * bottom bar, the breadcrumbs and the Dashboard's module list.
 * `permission` is checked with usePermissions().hasPermission(); items without one are always shown.
 */
export const NAV_GROUPS = [
    {
        id: 'home',
        label: null,
        items: [
            { id: 'dashboard', label: 'Overview', path: '/dashboard', icon: ICONS.dashboard, permission: 'view_dashboard', description: 'Today, KPIs and what needs attention' },
            // the assistant alone, without the Overview's figures (owner, 5 Oct 2026)
            { id: 'assistant', label: 'AI Assistant', path: '/assistant', icon: ICONS.robot, permission: 'ask_assistant', description: 'Find a customer or ask the assistant' },
            // every page as a card, in the user's own order (owner, 1 Oct 2026) - open to every signed-in user
            { id: 'modules', label: 'Modules', path: '/modules', icon: ICONS.list, description: 'Every page you can open, as cards in your order' },
        ],
    },
    {
        // the branch desk, every day
        id: 'operations',
        description: 'The daily desk: monthly contracts, airport, rental report, Operations Portal (calculators), history, customer automation, Brokers, Availability',
        label: 'Operations',
        icon: ICONS.operations,
        items: [
            { id: 'contracts', label: 'Monthly management', path: '/contracts', icon: ICONS.contracts, permission: 'view_contracts', description: 'Monthly contracts, search and follow-up' },
            { id: 'airport', label: 'Airport Branches', path: '/airport-dashboard', icon: ICONS.airport, permission: 'view_airport_dashboard', description: 'Terminal 1 and Terminal 2 contracts' },
            { id: 'daily-booking', label: 'Daily rental report', path: '/daily-booking', icon: ICONS.booking, permission: 'view_daily_booking', description: 'Contracts per day or range, cars summary, export' },
            { id: 'operations', label: 'Operations Portal', path: '/operations', icon: ICONS.reminder, permission: 'view_operations', description: 'Reminder (day 13, refunds), mileage, fuel and discount & tax calculators, backup' },
            { id: 'history', label: 'History Lens', path: '/report-history', icon: ICONS.reports, permission: 'view_history', description: 'Archive search, contract timeline, multi-car' },
            { id: 'automation', label: 'Customer automation', path: '/automation', icon: ICONS.automation, permission: 'use_automation', description: 'Read documents, prepare Ejar and TARS' },
            { id: 'rentway', label: 'Brokers', path: '/brokers', icon: ICONS.calendar, permission: 'view_ops_rentway', description: "Booking sites' Dubai reservations (Rentway + Comfy) checked against Ejar" },
            { id: 'availability', label: 'Availability', path: '/availability', icon: ICONS.fleet, permission: 'view_ops_availability', description: 'Airport cars free per model, day by day: bookings with no car, the last car, booking sites to close' },
        ],
    },
    {
        id: 'fleet',
        description: 'Vehicles, maintenance, service and government transactions',
        label: 'Fleet',
        icon: ICONS.fleet,
        items: [
            { id: 'fleet', label: 'Vehicles', path: '/fleet', icon: ICONS.fleet, permission: 'view_fleet_list', description: 'Fleet list, registration and expiry' },
            { id: 'maintenance', label: 'Maintenance', path: '/maintenance', icon: ICONS.maintenance, permission: 'view_maintenance', description: 'Service and repairs' },
            { id: 'service-history', label: 'Service history', path: '/fleet-service-history', icon: ICONS.history, permission: 'view_service_history', description: 'Vehicle service records' },
            { id: 'car-usage', label: 'Car usage', path: '/car-usage', icon: ICONS.matcher, permission: 'view_matcher', description: 'Who had each car, idle cars, repeated cars' },
            { id: 'government', label: 'Government & RTA', path: '/government', icon: ICONS.government, permission: 'view_government_transactions', description: 'RTA, traffic and government letters' },
        ],
    },
    {
        id: 'charges',
        description: 'Traffic fines, Salik / parking matching, invoices and Ejar charges',
        label: 'Fines & charges',
        icon: ICONS.fines,
        items: [
            { id: 'fines', label: 'Traffic fines', path: '/traffic-fines', icon: ICONS.fines, permission: 'view_traffic_fines', description: 'RTA fines, matching and images' },
            { id: 'invoices', label: 'Invoices', path: '/invoices', icon: ICONS.invoices, permission: 'view_invoices', description: 'Salik, parking and fines invoices' },
            { id: 'engine', label: 'The Engine', path: '/the-engine', icon: ICONS.engine, permission: 'view_engine', description: 'Register other charges to Ejar' },
        ],
    },
    {
        id: 'finance',
        description: 'Accounts: branch charges, custodies and KPIs',
        label: 'Finance',
        icon: ICONS.kpi,
        items: [
            { id: 'branch-charges', label: 'Branch charges', path: '/branch-charges', icon: ICONS.salik, permission: 'view_branch_charges', description: 'Fines, Salik, Darb, parking per branch' },
            { id: 'custodies', label: 'Custodies', path: '/custodies', icon: ICONS.safe, permission: 'view_custodies', description: 'Petty cash and expenses' },
            { id: 'kpis', label: 'KPIs', path: '/kpis', icon: ICONS.kpi, permission: 'view_kpi_dashboard', description: 'Performance and metrics' },
        ],
    },
    {
        id: 'admin',
        description: 'Ejar sync, staff and users',
        label: 'Admin',
        icon: ICONS.admin,
        items: [
            { id: 'database', label: 'Database', path: '/database', icon: ICONS.database, permission: 'view_database', description: 'Ejar sync and the staff import' },
            { id: 'staff', label: 'Staff', path: '/staff', icon: ICONS.staff, permission: 'view_staff', description: 'Employee management' },
            { id: 'admin', label: 'Administration', path: '/admin/users', icon: ICONS.admin, permission: 'manage_users', description: 'Users, roles and owner tools' },
        ],
    },
];

/* Pages that are not in the sidebar but still need a breadcrumb parent. */
export const ROUTE_META = [
    { path: '/admin/roles', label: 'Roles', parent: '/admin/users' },
    { path: '/custodies/', label: 'Custody', parent: '/custodies' },
    { path: '/database/', label: 'Importer', parent: '/database' },
    { path: '/change-password', label: 'Change password', parent: null },
];

/* Mobile bottom bar: the four most-used destinations; "Menu" opens the full navigation. */
export const MOBILE_TABS = ['dashboard', 'contracts', 'fleet', 'fines'];

/* Each labelled group has its own section page listing its modules (breadcrumb target). */
export const sectionPath = (groupId) => `/section/${groupId}`;
export const findNavGroup = (groupId) => NAV_GROUPS.find((g) => g.id === groupId && g.label) || null;

export const ALL_NAV_ITEMS = NAV_GROUPS.flatMap((g) => g.items.map((item) => ({ ...item, group: g.label, groupId: g.id })));

/** The first page in the menu's order that `can(permission)` opens - "/modules" (open to everyone) when none does. */
export const firstPagePath = (can) => (ALL_NAV_ITEMS.find((i) => i.permission && can(i.permission)) || { path: '/modules' }).path;

const groupCrumb = (item) => ({ label: item.group, path: sectionPath(item.groupId) });

export function findNavItem(pathname) {
    if (!pathname) return null;
    const exact = ALL_NAV_ITEMS.find((i) => i.path === pathname);
    if (exact) return exact;
    return ALL_NAV_ITEMS
        .filter((i) => pathname.startsWith(`${i.path}/`))
        .sort((a, b) => b.path.length - a.path.length)[0] || null;
}

/** Breadcrumb trail for a path: [{ label, path }] (the last entry is the current page). */
export function getBreadcrumbs(pathname) {
    const section = /^\/section\/([^/]+)/.exec(pathname || '');
    if (section) {
        const group = findNavGroup(section[1]);
        return group ? [{ label: group.label, path: pathname }] : [];
    }
    const meta = ROUTE_META.find((m) => (m.path.endsWith('/') ? pathname.startsWith(m.path) : pathname === m.path));
    const trail = [];
    if (meta) {
        const parent = meta.parent ? findNavItem(meta.parent) : null;
        if (parent) {
            if (parent.group) trail.push(groupCrumb(parent));
            trail.push({ label: parent.label, path: parent.path });
        }
        trail.push({ label: meta.label, path: pathname });
        return trail;
    }
    const item = findNavItem(pathname);
    if (item) {
        if (item.group) trail.push(groupCrumb(item));
        trail.push({ label: item.label, path: item.path });
    }
    return trail;
}
